import { db, LiveSession } from './audioUploadDB';
import { saveToQueue } from './audioUploadQueue';
import {
  getInvalidRecordingReason,
  MIN_RECORDING_BYTES,
  parseHHMMSS,
} from './recordingIntegrity';

// ── 진행 중 녹음 중간 저장 (PORTAL-12587) ────────────────────────────
// 녹음은 끝날 때까지 메모리(MediaRecorder)에만 모여, 도중에 브라우저가 죽거나 새로고침되면
// 전부 사라졌다. 녹음하는 동안 같은 마이크 스트림을 한 번 더 녹음해 일정 간격으로 조각을
// IndexedDB 에 쌓고, 비정상 종료로 남은 조각은 다음 진입 때 이어 붙여 백업 큐로 옮긴다.
// 그러면 기존 복구 안내(문서 재진입 · 온라인 복구)로 다시 올릴 수 있다.

/** 조각을 받는 간격 — 비정상 종료 시 잃는 최대 구간 */
export const LIVE_CHUNK_MS = 10000;

/**
 * Web Locks 를 못 쓰는 브라우저에서, 이 시간 넘게 조각이 안 쌓인 세션은 끝난 것으로 본다.
 * 녹음 중엔 LIVE_CHUNK_MS 마다 갱신되므로 넉넉히 둔다(일시정지 중엔 갱신이 멈춘다).
 */
const STALE_SESSION_MS = 6 * 60 * 60 * 1000;

export const liveSessionLock = (sessionId: string) =>
  `talkflow-live-${sessionId}`;

export const newLiveSessionId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * 세션을 기록한다. sessionId 를 미리 만들어 잠금을 먼저 쥐고 넘기면, 기록과 잠금 사이에
 * 다른 탭의 되살리기가 이 세션을 비정상 종료로 오인하는 틈이 없다.
 */
export const startLiveSession = async (
  meta: Pick<LiveSession, 'docId' | 'title' | 'mimeType'>,
  sessionId: string = newLiveSessionId(),
): Promise<string> => {
  const now = new Date();
  await db.liveSessions.add({
    ...meta,
    sessionId,
    startedAt: now,
    updatedAt: now,
  });
  return sessionId;
};

/** 조각 추가 — 이미 정리된 세션(정상 종료 뒤 늦게 온 마지막 조각)이면 버린다 */
export const appendLiveChunk = async (
  sessionId: string,
  seq: number,
  blob: Blob,
): Promise<void> => {
  if (blob.size === 0) return;
  await db.transaction('rw', db.liveSessions, db.liveChunks, async () => {
    const session = await db.liveSessions.get(sessionId);
    if (!session) return;
    await db.liveChunks.add({ sessionId, seq, blob });
    await db.liveSessions.update(sessionId, { updatedAt: new Date() });
  });
};

/** 정상 종료 — 전체 녹음이 업로드됐거나 백업 큐로 넘어갔으면 조각은 필요 없다 */
export const discardLiveSession = async (sessionId: string): Promise<void> => {
  await db.transaction('rw', db.liveSessions, db.liveChunks, async () => {
    await db.liveChunks.where('sessionId').equals(sessionId).delete();
    await db.liveSessions.delete(sessionId);
  });
};

/** 남은 조각을 이어 한 파일로 — 조각이 없으면 null */
export const assembleLiveSession = async (
  session: LiveSession,
): Promise<File | null> => {
  const chunks = await db.liveChunks
    .where('sessionId')
    .equals(session.sessionId)
    .sortBy('seq');
  if (chunks.length === 0) return null;
  const blob = new Blob(
    chunks.map((c) => c.blob),
    { type: session.mimeType || chunks[0].blob.type },
  );
  return new File([blob], session.title, { type: blob.type });
};

/**
 * 비정상 종료로 남은 녹음을 백업 큐로 옮긴다 — 옮긴 건수를 돌려준다.
 * isAlive 가 true 인 세션은 다른 탭에서 지금 녹음 중이라 건드리지 않는다.
 * isAlive 가 판단하지 못하면(undefined) 마지막 조각 시각으로 가른다.
 * 헤더뿐인 빈 조각 · 길이를 못 읽는 손상 파일은 업로드 가드와 같은 기준으로 버리고
 * onInvalid 로 알린다 — 올려도 같은 결과라 큐에 쌓지 않는다.
 */
export const recoverInterruptedSessions = async ({
  isAlive,
  getDuration,
  onInvalid,
  now = Date.now(),
}: {
  isAlive: (sessionId: string) => Promise<boolean | undefined>;
  getDuration: (file: File) => Promise<string>;
  onInvalid?: (session: LiveSession, reason: string, file: File) => void;
  now?: number;
}): Promise<number> => {
  const sessions = await db.liveSessions.toArray();
  let recovered = 0;
  for (const session of sessions) {
    const alive = await isAlive(session.sessionId);
    const stale =
      now - new Date(session.updatedAt).getTime() > STALE_SESSION_MS;
    if (alive === true || (alive === undefined && !stale)) continue;

    const file = await assembleLiveSession(session);
    if (file) {
      // 빈 파일은 길이를 읽어 볼 것도 없다(디코딩 타임아웃을 기다리지 않게)
      const duration =
        file.size >= MIN_RECORDING_BYTES ? await getDuration(file) : '00:00:00';
      const reason = getInvalidRecordingReason(file, parseHHMMSS(duration));
      if (reason) {
        onInvalid?.(session, reason, file);
      } else {
        await saveToQueue(session.docId, session.title, file, duration);
        recovered += 1;
      }
    }
    await discardLiveSession(session.sessionId);
  }
  return recovered;
};
